import { Component, Input, OnInit } from '@angular/core';
import { SolicitudRevocacionService } from '../../../../services/database/solicitud-revocacion.service';
import { ISolicitudRevocacion, RespuestaRevocacionFEDTO } from '../../../../shared/interfaces/database/firma_electronica';
import { FormGroupErrorMessageComponent, LoadingPanelService, noWhitespaceValidator, Utils } from 'lib-base';
import { firstValueFrom, forkJoin } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';

@Component({
  selector: 'app-revocacion-respuesta',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, ReactiveFormsModule, InputTextareaModule,
    FormGroupErrorMessageComponent, ButtonModule
  ],
  styles: [
    `
    .bg-blue-brand {
      background-color: #0C3D8DFF;
    }
    .text-custom-blue {
      color: #0C3D8DFF;
    }
    `
  ],
  template: `
  <section>
    <main *ngIf="solicitud" class="grid col-12">
      <!--#region SOLICITUD -->
      <div class="col-12 bg-blue-brand border-round-xl align-items-center inline-flex mb-1">
        <div class="border-round-3xl bg-white mx-4">
          <i class="pi pi-check text-custom-blue font-bold text-lg m-1"></i>
        </div>
        <p class="text-xl font-bold text-white">{{'msg.revocacionFirmaElectronica' | translate}}</p>
      </div>
      <div class="col-12 md:col-6">
        <div>
          <b>{{'common.motivoRevocacion' | translate}}:</b>
        </div>
        <div>
          <p>{{solicitud.motivoRevocacion}}</p>
        </div>
      </div>
      <div class="col-12 md:col-6">
        <div>
          <b>{{'msg.cuidadanoNombre' | translate}}:</b>
        </div>
        <div>
          <p>{{solicitud.nombreCompleto}}</p>
        </div>
      </div>
      <div class="col-12">
        <div>
          <b>{{'common.observaciones' | translate}}:</b>
        </div>
        <div>
          <p>{{solicitud.justificacionRevocacion}}</p>
        </div>
      </div>  
      <!--#endregion SOLICITUD -->
      <div class="col-12 pl-0 pr-0">
        <form [formGroup]="formRespuesta" *ngIf="formRespuesta" class="grid col-12">
          <div class="col-12">
            <div>
              <b>{{'common.seguimiento' | translate}}:</b>
            </div>
            <div>
              <textarea rows="5" pInputTextarea formControlName="observacion" class="w-full"></textarea>
              <ng-container
                  *ngIf="formRespuesta.get('observacion')?.invalid && formRespuesta.get('observacion')?.touched">
                  <app-form-group-error-message [control]="formRespuesta.get('observacion')">
                  </app-form-group-error-message>
              </ng-container>
            </div>
          </div>
          <div class="col-12 md:col-6">
            <button pButton type="button" icon="pi pi-thumbs-up" 
              [label]="'msg.aprobarSolicitud' | translate"  class="p-button-success p-button-outlined w-full"
              (click)="onResponderSolicitud(true)"></button>
          </div>
          <div class="col-12 md:col-6">
            <button pButton type="button" icon="pi pi-thumbs-down"
              [label]="'msg.rechazarSolicitud' | translate" class="p-button-danger p-button-outlined w-full"
              (click)="onResponderSolicitud(false)"></button>
          </div>
        </form>
      </div>
    </main>
  </section>
  `
})
export class RevocacionRespuestaComponent implements OnInit {
  @Input() solicitudId !: number;
  solicitud !: ISolicitudRevocacion;
  trans: any = {};
  formRespuesta !: FormGroup;

  constructor(private _srs: SolicitudRevocacionService, private _lps: LoadingPanelService, private _t: TranslateService,
    private _fb: FormBuilder, private _ms: MessageService, private readonly _router: Router
  ) {
    this.configureForm();
  }

  ngOnInit(): void {
    this.translate();
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      advertenciaCompletarCampos: this._t.get('msg.advertenciaCompletarCampos'),
      advertencia: this._t.get('msg.advertencia'),
      exito: this._t.get('common.exito'),
      actividadFinalizada: this._t.get('msg.actividadFinalizada')
    }));
  }

  loadComponent() {
    this.getSolicitud();
  }

  getSolicitud() {
    this._lps.show(true);
    this._srs.getSolicitud(this.solicitudId).subscribe({
      next: (data: ISolicitudRevocacion) => {
        this.solicitud = data;
      },
      complete: () => {
        this._lps.hide();
      }
    });
  }

  //#region Form
  configureForm() {
    this.formRespuesta = this._fb.group({
      observacion: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(255)]],
    });
  }

  respuestaSolicitud(request: RespuestaRevocacionFEDTO) {
    this._lps.show(true);
    this._srs.respuestaSolicitud(this.solicitudId, request).subscribe({
      next: () => {
        this._lps.hide();
        this._ms.add({ severity: 'success', summary: this.trans.exito, detail: this.trans.actividadFinalizada });
        this._router.navigate(['/configuracion/firma-electronica/revocaciones']);
      },
      complete: () => {
        this._lps.hide();
      }
    });
  }


  onResponderSolicitud(aprobada: boolean) {
    if (this.formRespuesta.invalid) {
      Utils.markAllFormControlsAsTouched(this.formRespuesta);
      this._ms.add({ severity: 'warn', summary: this.trans.advertencia, detail: this.trans.advertenciaCompletarCampos });
      return;
    }

    const request:RespuestaRevocacionFEDTO = {
      observacion: this.formRespuesta.get('observacion')?.value,
      aprobada: aprobada  
    }
    this.respuestaSolicitud(request);
  }

  // #endregion Form

}
